import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from '@angular/core';
import {
  type AbstractControl,
  FormBuilder,
  type FormControl,
  type FormGroup,
  FormsModule,
  ReactiveFormsModule,
  type ValidationErrors,
  type ValidatorFn,
  Validators,
} from '@angular/forms';
import { generateUUID, type UUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import { HelperTextComponent } from '@bizzdesign/ui-helper-text';
import { ButtonModule } from 'primeng/button';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { type SelectChangeEvent, SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { type CategorySelection } from '../../models/category-selection.model';
import { type ExistingCategories } from '../../models/existing-categories.model';

@Component({
  selector: 'bd-category-selector',
  imports: [
    NgClass,
    SharedI18nModule,
    ButtonModule,
    RadioButtonModule,
    SelectModule,
    FormsModule,
    ReactiveFormsModule,
    InputGroupModule,
    InputTextModule,
    HelperTextComponent,
    TooltipModule,
  ],
  templateUrl: './category-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategorySelectorComponent {
  private readonly fb = inject(FormBuilder);

  private _existingCategories: ExistingCategories | null = null;

  @Input() set existingCategories(value: ExistingCategories | null) {
    this._existingCategories = value;
    this.categoryForm.reset();
    const hasExistingCategories =
      this.existingCategories !== null &&
      this.existingCategories.categories.length > 0 &&
      this.existingCategories.categories.some(
        (category) => category.readOnly !== true,
      );
    this.categoryForm
      .get('selectNewCategory')!
      .setValue(!hasExistingCategories);
    this.emitNoSelectionEvent();
  }

  get existingCategories(): ExistingCategories | null {
    return this._existingCategories;
  }

  @Output() selectedCategory = new EventEmitter<CategorySelection>();

  categoryForm: FormGroup = this.fb.group({
    selectNewCategory: this.fb.control(false),
    existingCategoryName: this.fb.control<string | null>(null),
    newCategoryName: this.fb.control<string | null>(null, [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(30),
      this.uniqueCategoryValidator(
        () =>
          this.existingCategories?.categories.map(
            (category) => category.name,
          ) ?? [],
      ),
    ]),
  });

  get isNewCategorySelected(): boolean {
    return this.categoryForm.get('selectNewCategory')!.value;
  }

  get newCategoryControl() {
    return this.categoryForm.get('newCategoryName') as FormControl;
  }

  get errorKeys(): string[] {
    if (this.newCategoryControl?.errors) {
      return Object.keys(this.newCategoryControl.errors);
    }
    return [];
  }

  onDisabledOptionClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  onCategorySelectionChange(event: SelectChangeEvent) {
    if (this.existingCategories && event.value) {
      this.selectedCategory.emit({
        objectType: this.existingCategories.objectType,
        categoryId: event.value,
        categoryName: this.getCategoryNameById(
          this.existingCategories,
          event.value,
        ),
        isNewCategory: false,
      });
    } else {
      this.emitNoSelectionEvent();
    }
  }

  onSelectNewCategoryChange() {
    this.categoryForm.controls['newCategoryName'].reset();
    this.categoryForm.controls['existingCategoryName'].reset();
    this.emitNoSelectionEvent();
  }

  onClearCategoryNameClick() {
    this.newCategoryControl?.reset();
    this.emitNoSelectionEvent();
  }

  async onConfirmCategoryName() {
    if (this.existingCategories === null) {
      return;
    }
    this.formatNameValue(this.newCategoryControl);

    if (this.newCategoryControl?.valid) {
      this.selectedCategory.emit({
        objectType: this.existingCategories.objectType,
        categoryId: generateUUID(),
        categoryName: this.newCategoryControl.value,
        isNewCategory: true,
      });
    } else {
      this.emitNoSelectionEvent();
    }
  }

  getCategoryNameById(
    existingCategories: ExistingCategories,
    id: UUID,
  ): string | undefined {
    const foundCategory = existingCategories.categories.find(
      (category) => category.id === id,
    );
    return foundCategory ? foundCategory.name : undefined;
  }

  formatNameValue(control: AbstractControl) {
    if (control.value) {
      const formattedValue = control.value.trim().replace(/\s+/g, ' ');
      if (formattedValue !== control.value) {
        control.setValue(formattedValue);
      }
    }
  }

  uniqueCategoryValidator(existingCategoriesFn: () => string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const categoryExists = existingCategoriesFn().some(
        (category) =>
          category &&
          control.value &&
          category.toLowerCase() === control.value.toLowerCase(),
      );
      return categoryExists ? { unique: true } : null;
    };
  }

  private emitNoSelectionEvent() {
    if (this.existingCategories) {
      this.selectedCategory.emit({
        objectType: this.existingCategories.objectType,
      });
    }
  }
}
